export interface IframeProps {
  /**主应用标识 */
  sourceApp?: string
  /**AI 管理后台免密身份 */
  manageIdentity?: string
  /**AI 管理后台免密登录 token */
  accessToken?: string
  /**是否显示侧边栏 */
  sidebar?: boolean
  /**是否显示状态栏 */
  statusbar?: boolean
  /**是否显示公告栏 */
  noticebar?: boolean
}

interface IframeInitMessage {
  type?: string
  props?: IframeProps
}

interface IframeRouteChangePayload {
  path: string
  fullPath: string
  name?: string
  query: Record<string, unknown>
  params: Record<string, unknown>
}

export const IFRAME_SOURCE_APP = {
  AI_ADMIN: 'storybrooke-ai-admin-web',
  DETAIL_PREVIEW_HUB: 'proxy-manifest-customer-confirm',
} as const

export const IFRAME_MESSAGE_TYPE = {
  Init: 'agent:iframe:init',
  Close: 'agent:iframe:close',
  RouteChange: 'agent:iframe:route-change',
} as const

const props = reactive<IframeProps>({})
const isInIframe =
  typeof window !== 'undefined' &&
  !window.__POWERED_BY_WUJIE__ &&
  window.parent !== window
const isFromAiAdmin = computed(
  () => isInIframe && props.sourceApp === IFRAME_SOURCE_APP.AI_ADMIN,
)
const isFromDetailPreviewHub = computed(
  () => isInIframe && props.sourceApp === IFRAME_SOURCE_APP.DETAIL_PREVIEW_HUB,
)
let parentOrigin = ''
let pendingRouteChange: IframeRouteChangePayload | undefined
let resolveIframeProps: ((value: IframeProps | undefined) => void) | undefined
const iframePropsReady = new Promise<IframeProps | undefined>((resolve) => {
  resolveIframeProps = resolve
})

if (typeof window !== 'undefined') {
  window.addEventListener(
    'message',
    (event: MessageEvent<IframeInitMessage>) => {
      if (!isInIframe) return
      if (event.source !== window.parent) return
      if (event.data?.type !== IFRAME_MESSAGE_TYPE.Init) return

      parentOrigin = event.origin
      Object.assign(props, event.data.props || {})
      resolveIframeProps?.(props)
      resolveIframeProps = undefined
      flushPendingRouteChange()
    },
  )
}

const waitForIframeProps = () => {
  if (typeof window === 'undefined' || !isInIframe || props.sourceApp) {
    return Promise.resolve(props)
  }

  return Promise.race([
    iframePropsReady,
    new Promise<undefined>((resolve) => {
      window.setTimeout(() => resolve(undefined), 3000)
    }),
  ])
}

const getIdentityHeaders = () => ({
  'manage-identity': props.manageIdentity,
  'access-token': props.accessToken,
})

const getBackRouteName = (isDraftPage: boolean) =>
  isDraftPage ? 'Draft' : 'Send'

const close = () => {
  if (!parentOrigin) return

  window.parent.postMessage({ type: IFRAME_MESSAGE_TYPE.Close }, parentOrigin)
}

const notifyRouteChange = (route: {
  path: string
  fullPath: string
  name?: string
  query: Record<string, unknown>
  params: Record<string, unknown>
}) => {
  if (!parentOrigin) {
    pendingRouteChange = route
    return
  }

  postRouteChange(route)
}

const postRouteChange = (route: IframeRouteChangePayload) => {
  if (!parentOrigin) return

  window.parent.postMessage(
    {
      type: IFRAME_MESSAGE_TYPE.RouteChange,
      route,
    },
    parentOrigin,
  )
}

const flushPendingRouteChange = () => {
  if (!pendingRouteChange) return

  postRouteChange(pendingRouteChange)
  pendingRouteChange = undefined
}

export const useIframe = () => ({
  isInIframe,
  get isFromAiAdmin() {
    return isFromAiAdmin.value
  },
  /**是否从详情预览中心主应用打开 */
  get isFromDetailPreviewHub() {
    return isFromDetailPreviewHub.value
  },
  props,
  waitForIframeProps,
  getIdentityHeaders,
  getBackRouteName,
  close,
  notifyRouteChange,
})
